// Interim / Advance / Final Payment Certificates (IPC) built to the FIDIC
// Conditions of Contract for Construction, Second Edition 2017, Clause 14
// [Contract Price and Payment]. The interim/final build-up follows the Statement
// items of Sub-Clause 14.3 and the certification of Sub-Clause 14.6.

import type { AssembledContract, Block, ProjectBrief } from "./types";
import { amountToWords } from "./amountToWords";

export type IpcKind = "advance" | "interim" | "final";

/** Cumulative valuation amounts to the end of the payment period (14.3). */
export interface IpcValuation {
  worksExecuted?: number; // 14.3(a) value of permanent Works measured
  materialsOnSite?: number; // 14.5 Plant & Materials
  variations?: number; // Clause 13 Variations
  provisionalSums?: number; // 13.4 Provisional Sums
  dayworks?: number;
  priceAdjustment?: number; // 13.7 legislation / 13.8 cost adjustment
  otherAdditions?: number; // claims and other additions
}

export interface IpcData {
  kind: IpcKind;
  certificateNo: string;
  date: string;
  currency: string;
  amountWords?: string;

  // Contractor's Statement (14.3)
  statementRef?: string;
  statementDate?: string;
  periodFrom?: string;
  periodTo?: string;

  // Interim / Final build-up (all cumulative to date)
  valuation?: IpcValuation;
  retentionPercent?: number; // 14.3
  retentionLimitPercent?: number; // 14.3 limit as % of Accepted Contract Amount
  retentionReleased?: number; // 14.9 cumulative retention released
  advanceRecoveredToDate?: number; // 14.2.3 cumulative advance recovered
  previouslyCertified?: number; // 14.3(g) amounts certified in previous IPCs
  vatPercent?: number;
  paymentDueDays?: number; // 14.7(b)

  // Advance Payment certificate specifics (14.2)
  advancePercent?: number;
  installmentIndex?: number;
  installmentCount?: number;
  advanceAmount?: number; // amount disbursed by this advance certificate
  guaranteeRef?: string; // Advance Payment Guarantee reference (14.2.1)

  notes?: string;
  engineerName?: string;
}

export interface IpcSummary {
  gross: number;
  retentionHeld: number;
  advanceRecovered: number;
  netToDate: number;
  previouslyCertified: number;
  dueThisCert: number;
  vat: number;
  totalPayable: number;
  belowMinimum: boolean;
}

const n = (v?: number) => (v == null || !isFinite(v) ? 0 : v);

/** Compute the FIDIC 14 payment build-up for interim/final certificates. */
export function computeIpc(b: ProjectBrief, ipc: IpcData): IpcSummary {
  const v = ipc.valuation || {};
  const gross = n(v.worksExecuted) + n(v.materialsOnSite) + n(v.variations) + n(v.provisionalSums) + n(v.dayworks) + n(v.priceAdjustment) + n(v.otherAdditions);
  const contractAmt = n(b.contractPrice?.amount);
  const retPct = ipc.retentionPercent ?? b.retentionPercent ?? 0;
  const capPct = ipc.retentionLimitPercent ?? b.retentionCapPercent;
  let retentionHeld = (retPct / 100) * gross;
  if (capPct != null && contractAmt > 0) retentionHeld = Math.min(retentionHeld, (capPct / 100) * contractAmt);
  retentionHeld = Math.max(0, retentionHeld - n(ipc.retentionReleased));
  const advanceRecovered = n(ipc.advanceRecoveredToDate);
  const netToDate = gross - retentionHeld - advanceRecovered;
  const previouslyCertified = n(ipc.previouslyCertified);
  const dueThisCert = netToDate - previouslyCertified;
  const vat = ipc.vatPercent ? (ipc.vatPercent / 100) * dueThisCert : 0;
  const totalPayable = dueThisCert + vat;
  const belowMinimum = b.minimumIpc != null && isFinite(b.minimumIpc.amount) && dueThisCert < b.minimumIpc.amount && ipc.kind === "interim";
  return { gross, retentionHeld, advanceRecovered, netToDate, previouslyCertified, dueThisCert, vat, totalPayable, belowMinimum };
}

/** Net amount certified by this certificate (used for headers/labels). */
export function netCertified(b: ProjectBrief | null, ipc: IpcData): number {
  if (ipc.kind === "advance") return n(ipc.advanceAmount);
  return b ? computeIpc(b, ipc).totalPayable : 0;
}

export function assembleIpc(b: ProjectBrief, ipc: IpcData): AssembledContract {
  const ccy = ipc.currency || b.contractPrice.currency;
  // Number.isFinite guard: a brief/IPC round-tripped through JSON turns NaN into
  // null, which would otherwise crash the render on .toLocaleString().
  const fmt = (x: number) => (Number.isFinite(x) ? `${ccy} ${x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${ccy} —`);
  const neg = (x: number) => (x ? `(${fmt(Math.abs(x))})` : fmt(0));
  const kindLabel = ipc.kind === "advance" ? "Advance Payment Certificate" : ipc.kind === "final" ? "Final Payment Certificate" : "Interim Payment Certificate";
  const paymentDays = ipc.paymentDueDays ?? b.paymentPeriodDays;
  // Amount in words is ALWAYS derived deterministically from the figure and
  // currency, so it can never disagree with the certified amount.
  const wordsFor = (amt: number) => amountToWords({ amount: amt, currency: ccy });

  const header: Block = {
    type: "table",
    columns: ["Field", "Detail"],
    rows: [
      ["Project", b.projectName || "—"],
      ["Contract No.", b.contractReference || "—"],
      ["Employer", b.employer.name || "—"],
      ["Contractor", b.contractor.name || "—"],
      ["Engineer", ipc.engineerName || b.engineer?.name || "—"],
      ["Certificate No.", ipc.certificateNo || "—"],
      ["Certificate date", ipc.date || "—"],
      ["Contractor's Statement (14.3)", `${ipc.statementRef || "—"}${ipc.statementDate ? ` dated ${ipc.statementDate}` : ""}`],
      ["Payment period", `${ipc.periodFrom || "—"} to ${ipc.periodTo || "—"}`],
      ["Currency", ccy],
    ],
  };

  const blocks: Block[] = [
    { type: "heading", text: `Certificate of Payment — ${kindLabel}`, level: 1 },
    { type: "paragraph", text: `Issued under Clause 14 [Contract Price and Payment] of the FIDIC Conditions of Contract for Construction, Second Edition 2017.` },
    header,
  ];

  if (ipc.kind === "advance") {
    blocks.push(
      { type: "heading", text: "Advance Payment (Sub-Clause 14.2)", level: 2 },
      { type: "paragraph", text: `This is an Advance Payment Certificate issued under Sub-Clause 14.2 [Advance Payment]. It certifies an advance (mobilisation) payment to the Contractor and does NOT include any valuation of the Works. No value of Works executed has been injected into this Certificate; the Contractor's entitlement arises solely from the Advance Payment provision, secured by the Advance Payment Guarantee under Sub-Clause 14.2.1.` },
      {
        type: "table",
        columns: ["Item", "Detail"],
        rows: [
          ["Nature of certificate", "Advance Payment only — no Works value certified"],
          ["Value of Works executed and certified to date", "Nil (not applicable to this Certificate)"],
          ["Advance Payment Guarantee (14.2.1)", ipc.guaranteeRef || "To be provided / on file"],
          ["Total advance payment", ipc.advancePercent != null ? `${ipc.advancePercent}% of the Accepted Contract Amount` : "As stated in the Contract Data"],
          ["This installment", `No. ${ipc.installmentIndex || "__"} of ${ipc.installmentCount || "__"}`],
        ],
      },
      {
        type: "table",
        columns: ["Description", `Amount (${ccy})`],
        rows: [
          ["Value of the Works executed (14.3)", fmt(0)],
          ["Advance Payment certified this Certificate (14.2)", fmt(n(ipc.advanceAmount))],
          ["Amount due to the Contractor this Certificate", fmt(n(ipc.advanceAmount))],
        ],
      },
      { type: "paragraph", text: `The Engineer hereby certifies that the amount due to the Contractor under this Advance Payment Certificate is ${wordsFor(n(ipc.advanceAmount)) ? wordsFor(n(ipc.advanceAmount)) + " — " : ""}${fmt(n(ipc.advanceAmount))}, exclusive of VAT. VAT shall be added in accordance with the prevailing tax legislation at the time of invoicing. This Certificate is issued subject to a valid and enforceable Advance Payment Guarantee remaining in force.` },
      { type: "paragraph", text: `The advance payment shall be recovered by percentage deductions from subsequent Interim Payment Certificates in accordance with Sub-Clause 14.2.3, once the value of Works certified reaches the threshold stated in the Contract Data, and shall be fully recovered before issue of the Taking-Over Certificate.` }
    );
    if (paymentDays) blocks.push({ type: "paragraph", text: `Payment shall be made by the Employer to the Contractor within ${paymentDays} days in accordance with Sub-Clause 14.7 [Payment].` });
  } else {
    const s = computeIpc(b, ipc);
    const v = ipc.valuation || {};
    blocks.push(
      { type: "heading", text: "A. Valuation of the Works to date (Sub-Clause 14.3)", level: 2 },
      {
        type: "table",
        columns: ["Item", "Sub-Clause", `Amount (${ccy})`],
        rows: [
          ["Value of the permanent Works executed", "14.3(a)", fmt(n(v.worksExecuted))],
          ["Plant and Materials", "14.5", fmt(n(v.materialsOnSite))],
          ["Variations", "13.3", fmt(n(v.variations))],
          ["Provisional Sums", "13.4", fmt(n(v.provisionalSums))],
          ["Dayworks", "13.5", fmt(n(v.dayworks))],
          ["Adjustments (legislation / cost)", "13.6 / 13.7", fmt(n(v.priceAdjustment))],
          ["Other additions (claims, etc.)", "—", fmt(n(v.otherAdditions))],
          ["Gross value of work done to date", "", fmt(s.gross)],
        ],
      },
      { type: "heading", text: "B. Retention (Sub-Clause 14.3 / 14.9)", level: 2 },
      {
        type: "table",
        columns: ["Item", `Amount (${ccy})`],
        rows: [
          [`Retention at ${ipc.retentionPercent ?? b.retentionPercent ?? "__"}%${(ipc.retentionLimitPercent ?? b.retentionCapPercent) != null ? `, limited to ${ipc.retentionLimitPercent ?? b.retentionCapPercent}% of the Accepted Contract Amount` : ""}`, fmt((n(v.worksExecuted) + n(v.materialsOnSite) + n(v.variations) + n(v.provisionalSums) + n(v.dayworks) + n(v.priceAdjustment) + n(v.otherAdditions)) * ((ipc.retentionPercent ?? b.retentionPercent ?? 0) / 100))],
          ["Less retention released (14.9)", fmt(n(ipc.retentionReleased))],
          ["Net retention held to date", fmt(s.retentionHeld)],
        ],
      },
      { type: "heading", text: "Payment Summary (Sub-Clause 14.6)", level: 2 },
      {
        type: "table",
        columns: ["Description", `Amount (${ccy})`],
        rows: [
          ["Gross value of work done to date", fmt(s.gross)],
          ["Less: retention held to date", neg(s.retentionHeld)],
          ["Less: advance payment recovered to date (14.2.3)", neg(s.advanceRecovered)],
          ["Net value certified to date", fmt(s.netToDate)],
          ["Less: amounts certified in previous certificates (14.3(g))", neg(s.previouslyCertified)],
          ["Amount due this Certificate (excl. VAT)", fmt(s.dueThisCert)],
          ...(ipc.vatPercent ? ([[`Add: VAT at ${ipc.vatPercent}%`, fmt(s.vat)]] as string[][]) : []),
          [`Total payable this Certificate${ipc.vatPercent ? " (incl. VAT)" : " (excl. VAT)"}`, fmt(s.totalPayable)],
        ],
      },
      { type: "paragraph", text: `The Engineer hereby certifies, pursuant to Sub-Clause 14.6 [Issue of IPC]${ipc.kind === "final" ? " and Sub-Clause 14.13 [Issue of the Final Payment Certificate]" : ""}, that the amount due to the Contractor under this Certificate is ${wordsFor(s.totalPayable) ? wordsFor(s.totalPayable) + " — " : ""}${fmt(s.totalPayable)}${ipc.vatPercent ? "" : ", exclusive of VAT"}.` }
    );
    if (s.belowMinimum && b.minimumIpc) {
      blocks.push({ type: "paragraph", text: `Note: the amount due is below the minimum amount of an Interim Payment Certificate stated in the Contract Data (${fmt(b.minimumIpc.amount)}) under Sub-Clause 14.6.2; certification may be withheld and carried to the next Statement.` });
    }
    if (paymentDays) {
      blocks.push({ type: "paragraph", text: `Payment shall be made by the Employer to the Contractor within ${paymentDays} days in accordance with Sub-Clause 14.7 [Payment].` });
    }
    blocks.push({ type: "paragraph", text: `This Certificate is issued for the purpose of interim payment only. In accordance with Sub-Clause 14.6.3, a Payment Certificate shall not be deemed to indicate the Engineer's acceptance, approval, consent or satisfaction with the Works.` });
  }

  if (ipc.notes) blocks.push({ type: "paragraph", text: ipc.notes });

  blocks.push({
    type: "signblock",
    parties: [
      { role: "Engineer (certifying)", name: ipc.engineerName || b.engineer?.name },
      { role: "Contractor (received)", name: b.contractor.representative },
    ],
  });

  const certifiedAmount = netCertified(b, ipc);
  return {
    title: `${b.projectName || "Project"} — ${kindLabel} ${ipc.certificateNo || ""}`.trim(),
    standardLabel: "FIDIC Red Book 2017 — Clause 14 [Contract Price and Payment]",
    masterCover: {
      kicker: "IPC · " + (ipc.kind === "advance" ? "Advance Payment" : ipc.kind === "final" ? "Final Payment" : "Interim Payment"),
      title: "Certificate of Payment",
      subtitle: b.projectName,
      contractLabel: kindLabel.toUpperCase(),
      standardLine: "FIDIC RED BOOK 2017 — CLAUSE 14",
      date: ipc.date,
      meta: [
        { label: "Certificate No.", value: ipc.certificateNo || "—" },
        { label: "Contract No.", value: b.contractReference || "—" },
        { label: "Contractor", value: b.contractor.name || "—" },
        ...(ipc.kind === "advance" ? [{ label: "Works value", value: "Nil (advance payment only)" }] : []),
        { label: "Amount certified", value: fmt(certifiedAmount) },
        { label: "Payment due", value: paymentDays ? `Within ${paymentDays} days (14.7)` : "—" },
      ],
    },
    parts: [{ id: "ipc", name: kindLabel, cover: { kicker: "Certificate", title: "Certificate of Payment", subtitle: `${kindLabel}${ipc.certificateNo ? ` — ${ipc.certificateNo}` : ""}` }, blocks }],
    notice: "Certificate issued under Clause 14 of the Conditions of Contract. Amounts are exclusive of VAT unless stated. This certificate does not, under Sub-Clause 14.6.3, indicate acceptance or approval of the Works. Not legal or financial advice.",
  };
}
